/**
 * Sync hook: runs the same logic as ExploreProvider and writes into the Zustand store.
 * Call this once at the explore page/layout (where you would wrap with ExploreProvider).
 */
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import {
  adaptPayload,
  prepareActiveFilter,
  prepareFilterSchema,
} from "@utils/adaptedpayloadExplore";
import { EXPLORE_SLUG } from "types/api/explore";
import newExploreApi from "@api/new-explore";
import { COURSE_TYPE } from "@types";
import { exploreMethodBySlug } from "types/api/new-explore";
import {
  ExploreInputDT,
  ExploreInstructorDT,
} from "@templates/new-explore/hooks/types";
import { ActiveFilterType } from "@templates/new-explore/hooks/useExloreStore";
import { ScrollTop } from "@templates/new-explore/hooks/helpers";
import useExplorePreloadedData from "./useExplorePreloadedData";
import { HIDDEN_PARAMS_URL } from "@modules/new-explore/schema";
import {
  isApiSortInQuery,
  normalizeExploreSortQuery,
} from "enums/new-explore/explore-sort";
import useQueryParams, { NAVIGATION_MODE } from "@shared/hooks/useQueryParams";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";

export function useExploreStoreSync() {
  const { query } = useRouter();
  const { setParams, clearParams, removeParamByKey } = useQueryParams();
  const { actions, paginationData } = useExplorePreloadedData();

  const slug =
    typeof query.slug === "string"
      ? (query.slug as EXPLORE_SLUG)
      : EXPLORE_SLUG.COURSE;
  const payload = adaptPayload(query);

  const { data: exploreData, error: exploreError } = useRequestImmutable(
    newExploreApi[exploreMethodBySlug[slug]](payload) as ExploreInputDT
  );

  const instructorDataHandler = () => {
    if (
      Object.keys(payload).find(
        (d) =>
          d === "instructor_id" && exploreData?.filters?.instructor_id?.length
      )
    ) {
      return exploreData?.filters?.instructor_id as ExploreInstructorDT[];
    }
  };

  const [instructorList, setInstructorList] = useState(
    instructorDataHandler() || []
  );

  const { data: categoryData } = useRequestImmutable(
    newExploreApi.getCategory()
  );

  // Sync exploreError into store
  useEffect(() => {
    actions.setExploreError(exploreError);
  }, [exploreError, actions]);

  useEffect(() => {
    actions.setFiltersData(prepareFilterSchema(payload, slug));
    if (payload?.type?.includes(COURSE_TYPE.EXAM)) {
      if (payload?.duration || payload?.publish_status) {
        removeParamByKey(["duration", "publish_status"], true);
      }
    }

    // Rewrite mistaken API sort values in the URL (e.g. most_sold → popular)
    const rawSort = typeof query.sort === "string" ? query.sort : undefined;
    if (rawSort && isApiSortInQuery(rawSort)) {
      setParams({
        data: [{ key: "sort", value: normalizeExploreSortQuery(rawSort) }],
        redirect: true,
        shallow: false,
        mode: NAVIGATION_MODE.REPLACE,
      });
      return;
    }

    if (payload?.isOld) {
      clearParams();
      setParams({
        data: Object.entries(payload).reduce<
          Array<{ key: string; value: string | string[] }>
        >((params, [key, value]) => {
          if (key === "isOld" || HIDDEN_PARAMS_URL.includes(key)) {
            return params;
          }

          params.push({
            key: key,
            value:
              key === "sort"
                ? normalizeExploreSortQuery(String(value))
                : (value as string | string[]),
          });

          return params;
        }, []),
        redirect: true,
        shallow: false,
        mode: NAVIGATION_MODE.REPLACE,
      });
    }
  }, [query]);

  useEffect(() => {
    if (payload && exploreData?.filters) {
      if (
        Object.keys(payload).find(
          (d) =>
            d === "instructor_id" && exploreData?.filters?.instructor_id?.length
        )
      ) {
        setInstructorList(
          exploreData?.filters ? exploreData?.filters?.instructor_id : []
        );
      }
      getInstructors().then((instructors) => {
        const filterData = prepareActiveFilter(
          payload,
          exploreData,
          instructors,
          categoryData
        );
        // @ts-ignore
        const { count, ...passingActiveFilters } = filterData;
        actions.setActiveFilters(passingActiveFilters as ActiveFilterType);
      });
    }

    if (!exploreData) {
      return;
    }

    const currentPage = paginationData?.page;
    if (currentPage !== exploreData.pagination?.page) ScrollTop();

    actions.setPaginationData(exploreData.pagination);
    const filterData = prepareFilterSchema(payload, slug);
    const activeFilterData = prepareActiveFilter(
      payload,
      exploreData,
      instructorList,
      categoryData
    );

    // @ts-ignore
    const { count, ...passingActiveFilters } = activeFilterData;
    actions.setActiveFilters(passingActiveFilters as ActiveFilterType);
    actions.setFiltersData(filterData);
    if (exploreData?.filters) {
      actions.setCategoryData(categoryData?.data);
    }
  }, [exploreData, categoryData, slug]);

  async function getInstructors() {
    try {
      if (
        instructorList.length &&
        instructorList.every((teacher) =>
          String(teacher.id)?.includes(payload["instructor_id"])
        )
      ) {
        return instructorList;
      } else {
        if (Object.keys(payload).find((d) => d === "instructor_id")) {
          const res = await newExploreApi
            .getInstructor(payload["instructor_id"])
            .fetch();
          setInstructorList(res);
          return res;
        } else {
          return [];
        }
      }
    } catch (error) {
      console.error(error);
      return [];
    }
  }
}
